import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(isoWeek)

export const REPORTING_PERIOD = {
    DAY: 'day',
    WEEK: 'week',
    MONTH: 'month',
    CUSTOM: 'custom',
}

export const REPORTING_PERIOD_OPTIONS = [
    { value: REPORTING_PERIOD.DAY, label: 'Day' },
    { value: REPORTING_PERIOD.WEEK, label: 'Week' },
    { value: REPORTING_PERIOD.MONTH, label: 'Month' },
    { value: REPORTING_PERIOD.CUSTOM, label: 'Custom' },
]

/** Default reporting range: today (day period). */
export const getDefaultReportingDateRange = () =>
    getReportingDateRangeForPeriod(REPORTING_PERIOD.DAY)

/**
 * Resolve start/end timestamps for a reporting period.
 * @param {'day'|'week'|'month'|'custom'} period
 * @param {dayjs.ConfigType} [referenceDate]
 */
export const getReportingDateRangeForPeriod = (
    period,
    referenceDate = dayjs()
) => {
    const ref = dayjs(referenceDate).isValid() ? dayjs(referenceDate) : dayjs()

    switch (period) {
        case REPORTING_PERIOD.DAY:
            return {
                start: ref.startOf('day').valueOf(),
                end: ref.endOf('day').valueOf(),
            }
        case REPORTING_PERIOD.WEEK:
            return {
                start: ref.startOf('isoWeek').valueOf(),
                end: ref.endOf('isoWeek').valueOf(),
            }
        case REPORTING_PERIOD.MONTH:
            return {
                start: ref.startOf('month').valueOf(),
                end: ref.endOf('month').valueOf(),
            }
        case REPORTING_PERIOD.CUSTOM:
        default:
            return {
                start: ref.startOf('month').valueOf(),
                end: ref.endOf('month').valueOf(),
            }
    }
}

/** Normalize picker / stored values to millisecond timestamps for API payload. */
export const toReportingDateTimestamp = (value, boundary = 'start') => {
    if (value == null || value === '') return null
    const d =
        typeof value === 'number' && Number.isFinite(value)
            ? dayjs(value)
            : dayjs(value)
    if (!d.isValid()) return null
    return boundary === 'end' ? d.endOf('day').valueOf() : d.startOf('day').valueOf()
}

export const formatLogDuration = (ms) => {
    const n = Number(ms)
    if (!Number.isFinite(n) || n <= 0) return '0h'
    const totalMinutes = Math.round(n / (1000 * 60))
    if (totalMinutes < 60) return `${totalMinutes}m`
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    if (minutes === 0) return `${hours}h`
    return `${hours}h ${minutes}m`
}

/** Full duration label for reporting progress rows (e.g. 27h 59m 21s). */
export const formatLogDurationDetailed = (ms) => {
    const n = Number(ms)
    if (!Number.isFinite(n) || n <= 0) return '0h 0m 0s'
    const totalSeconds = Math.floor(n / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    return `${hours}h ${minutes}m ${seconds}s`
}

const DEFAULT_PROJECT_BAR_COLOR = '#94a3b8'

/** Build stacked bar segments from a member's project logs. */
export const getLogProgressSegments = (record) => {
    const total = Number(record?.totalLog) || 0
    const projects = Array.isArray(record?.projects) ? record.projects : []

    const fromProjects = projects
        .map((p) => ({
            key: p?.projectId || p?.projectName,
            ms: Number(p?.totalLog) || 0,
            color: p?.colorCode || DEFAULT_PROJECT_BAR_COLOR,
        }))
        .filter((s) => s.ms > 0)

    if (fromProjects.length > 0) {
        return { total, segments: fromProjects }
    }

    if (total > 0) {
        return {
            total,
            segments: [
                { key: 'total', ms: total, color: DEFAULT_PROJECT_BAR_COLOR },
            ],
        }
    }

    return { total: 0, segments: [] }
}

export const formatReportingDisplayDate = (dateStr) => {
    if (!dateStr) return '—'
    const parsed = dayjs(dateStr, 'DD-MM-YYYY')
    return parsed.isValid() ? parsed.format('D MMM YYYY') : String(dateStr)
}

export const getInitials = (name) => {
    if (!name) return 'U'
    const parts = String(name).trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    }
    return String(name).slice(0, 2).toUpperCase()
}
